import React, { useMemo, useRef, useState } from 'react';
import {
  Button,
  MenuToggle,
  MenuToggleElement,
  Select,
  SelectList,
  SelectOption,
  TextInputGroup,
  TextInputGroupMain,
  TextInputGroupUtilities,
} from '@patternfly/react-core';
import { TimesIcon } from '@patternfly/react-icons';
import { useTranslation } from 'react-i18next';
import TIMEZONE_LOCALES from '../../../config/timezones';

type Props = {
  onChange?: (value: string) => void;
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  readonly?: boolean;
  timezones?: Record<string, string>;
};

const TimezoneWidget = ({
  onChange,
  value,
  placeholder,
  disabled,
  readonly,
  timezones = TIMEZONE_LOCALES,
}: Props) => {
  const { t } = useTranslation();
  const isDisabled = Boolean(disabled || readonly);

  const [isOpen, setIsOpen] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const textInputRef = useRef<HTMLInputElement>(null);

  const entries = useMemo(() => Object.entries(timezones), [timezones]);

  const selectedLabel = useMemo(() => {
    if (!value) return '';
    const match = entries.find(([, iana]) => iana === value);
    return match ? match[0] : value;
  }, [value, entries]);

  const filteredEntries = useMemo(() => {
    if (!filterValue) return entries;
    const lower = filterValue.toLowerCase();
    return entries.filter(([label]) => label.toLowerCase().includes(lower));
  }, [entries, filterValue]);

  const onToggle = () => {
    if (!isDisabled) setIsOpen((prev) => !prev);
  };

  const onSelect = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    selectedValue: string | number | undefined,
  ) => {
    const iana = String(selectedValue);
    onChange?.(iana);
    setFilterValue('');
    setIsOpen(false);
    textInputRef.current?.focus();
  };

  const onTextInputChange = (
    _event: React.FormEvent<HTMLInputElement>,
    val: string,
  ) => {
    setFilterValue(val);
    if (!isOpen) setIsOpen(true);
  };

  const onClear = () => {
    setFilterValue('');
    onChange?.('');
    textInputRef.current?.focus();
  };

  const toggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      variant="typeahead"
      onClick={onToggle}
      isExpanded={isOpen}
      isDisabled={isDisabled}
      isFullWidth
    >
      <TextInputGroup isPlain>
        <TextInputGroupMain
          value={isOpen ? filterValue : filterValue || selectedLabel}
          onChange={onTextInputChange}
          onFocus={() => {
            if (!isOpen) setIsOpen(true);
          }}
          innerRef={textInputRef}
          placeholder={placeholder || t('Select timezone...')}
          autoComplete="off"
        />
        {(filterValue || value) && (
          <TextInputGroupUtilities>
            <Button variant="plain" onClick={onClear} aria-label={t('Clear')}>
              <TimesIcon />
            </Button>
          </TextInputGroupUtilities>
        )}
      </TextInputGroup>
    </MenuToggle>
  );

  return (
    <div data-testid="timezone-select">
      <Select
        id="timezone-select"
        isOpen={isOpen}
        selected={value}
        onSelect={onSelect}
        onOpenChange={setIsOpen}
        toggle={toggle}
        shouldFocusToggleOnSelect
      >
        <SelectList style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {filteredEntries.length > 0 ? (
            filteredEntries.map(([label, iana]) => (
              <SelectOption key={iana} value={iana}>
                {label}
              </SelectOption>
            ))
          ) : (
            <SelectOption isDisabled>{t('No timezones found')}</SelectOption>
          )}
        </SelectList>
      </Select>
    </div>
  );
};

export default TimezoneWidget;
